import React, { useCallback, useEffect, useState } from 'react';
import { storage } from '../../utils/platform';
import { API_BASE } from '../../config/api';
import { openExternalUrl, urlToWeb } from '../utils/external';
import SubPageHeader from '../components/SubPageHeader';
import SectionCard from '../components/SectionCard';
import MenuRow from '../components/MenuRow';

/**
 * MobileCompte — identité du compte : e-mail, mot de passe, langue, RGPD.
 *
 * Le changement d'e-mail suit le même flux vérifié que le web : mot de passe
 * exigé, lien de confirmation envoyé à la nouvelle adresse et alerte à
 * l'ancienne. Rien ne bascule tant que le lien n'est pas ouvert — sans ce sas,
 * une session volée suffirait à s'approprier le compte.
 *
 * ⚠️ Export et suppression de compte renvoient vers le web. Ce sont des actions
 * irréversibles qui méritent un grand écran, et le téléchargement d'un fichier
 * JSON depuis une WebView iOS n'offre aucune garantie d'aboutir.
 */
export default function MobileCompte() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const [emailForm, setEmailForm] = useState({ open: false, new_email: '', current_password: '' });
  const [pwdForm, setPwdForm] = useState({ open: false, current: '', next: '', confirm: '' });

  const headers = useCallback(async () => {
    const token = await storage.get('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/account/me`, { headers: await headers() });
      const p = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(p?.error || `HTTP ${res.status}`);
      setAccount(p);
    } catch (e) {
      setMsg({ type: 'error', text: 'Impossible de charger votre compte.' });
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const ERREURS = {
    invalid_current_password: 'Mot de passe actuel incorrect.',
    email_already_used_other: 'Cette adresse est déjà utilisée par un autre compte.',
    email_unchanged: "C'est déjà votre adresse actuelle.",
    invalid_email: 'Adresse e-mail invalide.',
    account_no_password: "Votre compte a été créé avec Google. Définissez d'abord un mot de passe via « Mot de passe oublié ».",
    current_password_required: 'Mot de passe requis.',
    password_too_short: 'Le mot de passe doit contenir au moins 8 caractères.',
  };

  const submitEmail = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/account/change-email`, {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify({
          new_email: emailForm.new_email.trim(),
          current_password: emailForm.current_password,
        }),
      });
      const p = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(p?.error), { code: p?.error });
      setMsg({ type: 'ok', text: `Lien de confirmation envoyé à ${emailForm.new_email.trim()}. Ouvrez-le pour valider.` });
      setEmailForm({ open: false, new_email: '', current_password: '' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: ERREURS[e.code] || 'Changement impossible.' });
    } finally { setBusy(false); }
  };

  const cancelEmail = async () => {
    setBusy(true); setMsg(null);
    try {
      await fetch(`${API_BASE}/api/account/cancel-email-change`, {
        method: 'POST', headers: await headers(),
      });
      setMsg({ type: 'ok', text: 'Demande annulée.' });
      await load();
    } finally { setBusy(false); }
  };

  const submitPassword = async () => {
    if (pwdForm.next.length < 8) {
      setMsg({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères.' });
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setMsg({ type: 'error', text: 'Les deux mots de passe ne correspondent pas.' });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: await headers(),
        body: JSON.stringify({
          mot_de_passe_actuel: pwdForm.current,
          nouveau_mot_de_passe: pwdForm.next,
        }),
      });
      const p = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(p?.error), { code: p?.error });
      setMsg({ type: 'ok', text: 'Mot de passe modifié.' });
      setPwdForm({ open: false, current: '', next: '', confirm: '' });
    } catch (e) {
      setMsg({ type: 'error', text: ERREURS[e.code] || 'Modification impossible.' });
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <SubPageHeader title="Mon compte" subtitle="Connexion et préférences" />
        <div style={styles.loading}>Chargement…</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <SubPageHeader title="Mon compte" subtitle="Connexion et préférences" />

      {msg && <div style={msg.type === 'error' ? styles.error : styles.ok}>{msg.text}</div>}

      <SectionCard title="✉️ Adresse e-mail">
        <div style={styles.block}>
          <div style={styles.currentValue}>{account?.email}</div>

          {account?.pending_email && (
            <div style={styles.pending}>
              <div>
                Changement en attente vers <strong>{account.pending_email}</strong>.
                Le lien expire après une heure.
              </div>
              <button type="button" style={styles.linkBtn} onClick={cancelEmail} disabled={busy}>
                Annuler la demande
              </button>
            </div>
          )}

          {!account?.has_password && (
            <p style={styles.helper}>
              Votre compte a été créé avec Google. Définissez d'abord un mot de passe
              via « Mot de passe oublié » pour pouvoir changer d'adresse.
            </p>
          )}

          {account?.has_password && !emailForm.open && (
            <button type="button" style={styles.primary}
                    onClick={() => { setEmailForm({ ...emailForm, open: true }); setMsg(null); }}>
              Modifier mon adresse
            </button>
          )}

          {emailForm.open && (
            <>
              <input
                type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
                placeholder="nouvelle@adresse.fr" style={styles.input}
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
              />
              <input
                type="password" placeholder="Votre mot de passe actuel" style={styles.input}
                value={emailForm.current_password}
                onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
              />
              <p style={styles.helper}>
                Un lien de confirmation partira vers la nouvelle adresse. Votre adresse
                actuelle reste active tant qu'il n'est pas ouvert.
              </p>
              <div style={styles.row}>
                <button type="button" style={styles.primary} onClick={submitEmail}
                        disabled={busy || !emailForm.new_email || !emailForm.current_password}>
                  {busy ? '…' : 'Envoyer le lien'}
                </button>
                <button type="button" style={styles.ghost}
                        onClick={() => setEmailForm({ open: false, new_email: '', current_password: '' })}>
                  Annuler
                </button>
              </div>
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard title="🔑 Mot de passe">
        <div style={styles.block}>
          {!account?.has_password ? (
            <p style={styles.helper}>
              Connexion via Google. Pour définir un mot de passe, utilisez « Mot de passe
              oublié » depuis l'écran de connexion.
            </p>
          ) : !pwdForm.open ? (
            <button type="button" style={styles.primary}
                    onClick={() => { setPwdForm({ ...pwdForm, open: true }); setMsg(null); }}>
              Modifier mon mot de passe
            </button>
          ) : (
            <>
              <input type="password" placeholder="Mot de passe actuel" style={styles.input}
                     value={pwdForm.current}
                     onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} />
              <input type="password" placeholder="Nouveau mot de passe (8 caractères min.)" style={styles.input}
                     value={pwdForm.next}
                     onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} />
              <input type="password" placeholder="Confirmer le nouveau mot de passe" style={styles.input}
                     value={pwdForm.confirm}
                     onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} />
              <div style={styles.row}>
                <button type="button" style={styles.primary} onClick={submitPassword} disabled={busy}>
                  {busy ? '…' : 'Enregistrer'}
                </button>
                <button type="button" style={styles.ghost}
                        onClick={() => setPwdForm({ open: false, current: '', next: '', confirm: '' })}>
                  Annuler
                </button>
              </div>
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Données personnelles">
        <MenuRow icon="⬇" label="Exporter mes données (web)"
                 onClick={() => openExternalUrl(urlToWeb('/compte'))} />
        <MenuRow icon="🗑" label="Supprimer mon compte (web)" variant="danger"
                 onClick={() => openExternalUrl(urlToWeb('/compte'))} />
      </SectionCard>
    </div>
  );
}

const styles = {
  page: { padding: '4px 0 24px' },
  loading: { padding: '40px 0', textAlign: 'center', color: '#6B6B6B', fontSize: 14 },
  block: { padding: 16 },
  currentValue: { fontSize: 15, fontWeight: 700, color: '#1A1A1A', marginBottom: 12, wordBreak: 'break-all' },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '11px 12px', marginBottom: 10,
    borderRadius: 10, border: '1px solid rgba(0,0,0,0.14)', fontFamily: 'inherit',
    // 16 px minimum : Safari iOS zoome au focus en dessous.
    fontSize: 16, background: '#FAF8F5',
  },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  primary: {
    flex: 1, minWidth: 140, padding: '12px 18px', borderRadius: 999, border: 'none',
    background: '#E8502A', color: '#FFFFFF', fontFamily: 'inherit',
    fontSize: 14.5, fontWeight: 700, cursor: 'pointer',
  },
  ghost: {
    flex: 1, minWidth: 100, padding: '12px 18px', borderRadius: 999,
    border: '1px solid rgba(0,0,0,0.14)', background: '#FFFFFF', color: '#1A1A1A',
    fontFamily: 'inherit', fontSize: 14.5, fontWeight: 600, cursor: 'pointer',
  },
  linkBtn: {
    marginTop: 8, background: 'none', border: 'none', padding: 0,
    color: '#C8442A', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
    textDecoration: 'underline', cursor: 'pointer',
  },
  helper: { fontSize: 12.5, lineHeight: 1.5, color: '#6B6B6B', margin: '0 0 12px' },
  pending: {
    padding: '10px 12px', borderRadius: 10, background: '#FFF8E8',
    border: '1px solid #F0D9A0', color: '#7A5A10', fontSize: 13,
    lineHeight: 1.5, marginBottom: 12,
  },
  ok: {
    padding: '12px 14px', borderRadius: 10, background: '#E8F5EE',
    border: '1px solid #A9DBB8', color: '#1E6B36', fontSize: 13.5, marginBottom: 12,
  },
  error: {
    padding: '12px 14px', borderRadius: 10, background: '#FDEAE5',
    border: '1px solid #F0B3AC', color: '#A32B1C', fontSize: 13.5, marginBottom: 12,
  },
};
